import { withDefaults } from '../../../core/theme';
import { tween } from '../../../core/effect/motion';
import { add, card, fade } from './common';
import { OBJECT_STYLE } from '../drawObjects';

const objectStyle = withDefaults(() => [OBJECT_STYLE]);

/**
 * **盤のカードが「浮いて開く」ように裏返る動き**（表示形式の変更 Issue #555 から切り出した。反転召喚 Issue #581 も使う）。
 * `effects/pos_change.js` の裏表が変わる動き（`motion: 'flip'`）と `effects/flipsummoning.js` の頭の 440ms がこれ。
 *
 * 少し浮いて、長い辺を軸に開くように裏返る（440ms。幅を縮めて開く形。影も一緒に）。
 * 前半（まだ前の面が見えている間）は演出の板（`flipFaceScript`）が**前の面**を描き、盤のカードは隠す。
 * 真横を過ぎたら盤のカード（＝後の面。盤面はイベントの後の状態）を残りの半回転ぶん回して下ろし、そのまま盤に返す（`flipTweak`）。
 *
 * ```
 * 0     盤のカードが浮く
 * 60    裏返り始める
 * 320   裏返り終わる → 400 着地（set_card）
 * 440   終わり
 * ```
 */
export const FLIP_D = 440;
export const FLIP_LAND = 400;
const LIFT_END = 110;
const FLIP_IN = 60;
const FLIP_OUT = 320;
const DROP = 300;

/** 裏返るときに浮く高さ（カードの幅に対する比） */
const LIFT_K = 0.4;

/**
 * 盤の上の回し（`stage/ObjectLayer.js::cardTurn` と同じ）。モンスターの守備表示は +90°、
 * **伏せた魔法・罠は +180°**（表 5 / 裏 10 はどちらも守備のビットが立つので、魔法・罠では守備を見ない）
 */
export const turnOf = (pos, spell = false) => {
  if (spell) return pos?.faceUp ? 0 : Math.PI;
  return pos?.defense ? Math.PI / 2 : 0;
};
/** 浮き（`drawObjects.js::card` と同じ規則。浮くのは表側攻撃表示のモンスターだけ） */
export const liftOf = (pos, style, spell = false) => {
  const v = objectStyle(style);
  return !spell && pos?.faceUp && !pos?.defense ? v.attackLift : v.cardLift;
};

/** 裏返り（0 = 前の面が上 → π = 後の面が上）と、浮き（盤の上の高さの足し分）と、向きの進み（0 → 1） */
export function flipAt(t, unit, spell = false) {
  const k = tween(t, FLIP_IN, FLIP_OUT, 'ease_in_out');
  const theta = Math.PI * k;
  const up = tween(t, 0, LIFT_END, 'back_out') * (1 - tween(t, DROP, FLIP_LAND, 'power2_in'));
  /*
   * 向きの進み。魔法・罠の伏せ（+180°）→ 表（0°）は**真横の瞬間に切り替える**
   * （180° 回した板は真横では同じ細い線なので継ぎ目が見えない。開きながら半周させない）
   */
  const yaw = spell ? (k < 0.5 ? 0 : 1) : k;
  return { theta, lift: unit * LIFT_K * up, yaw };
}

/**
 * **盤のカードの借り方**（`tweaks` の handle に 1 フレームぶん当てる）。
 * 盤のカードはイベントの後の状態（後の面・後の向き・後の浮き）で置かれているので、そこからの差で動かす。
 * 毎フレーム素の置き方から組む（`h.reset()`）。
 *
 * @param {any} h `stage/three/runtime.js::handleFor` の handle
 * @param {{ t: number, prev: any, next: any, spell?: boolean, style?: any }} o
 */
export function flipTweak(h, { t, prev, next, spell = false, style }) {
  h.reset?.();
  const part = h.part ?? {};
  const stack = h.stack ?? h.root;
  const s = h.root.scale.x || 1;
  const dYaw = turnOf(prev, spell) - turnOf(next, spell);
  const dLift = liftOf(prev, style, spell) - liftOf(next, style, spell);
  const f = flipAt(t, part.size?.w ?? 0, spell);
  h.root.rotation.y += dYaw * (1 - f.yaw);
  // 前半は演出の板（前の面）が見せる。盤のカードは真横を過ぎてから
  if (f.theta < Math.PI / 2) {
    (h.materials ?? []).forEach((m) => { m.opacity = 0; });
    return;
  }
  // 残りの半回転: 長い辺を軸に開く（幅が 0 → 1。影も一緒に）
  h.root.scale.x = s * Math.max(0.02, -Math.cos(f.theta));
  stack.position.y += f.lift + dLift * (1 - f.yaw);
}

/**
 * **前の面を描く板**（`space: 'board'` の層の script の中身）。前半だけ見せる。
 * 盤の位置が分からなければ空の root だけ返す。
 *
 * @param {any} ctx 層の script の ctx
 * @param {{ prev: any, next: any, spell?: boolean, code?: any, order?: number }} o `code` は前の面が表のときの絵
 */
export function flipFaceScript(ctx, { prev, next, spell = false, code = null, order = 20 }) {
  const { THREE, kit } = ctx;
  const root = new THREE.Group();
  const { payload } = ctx;
  const f = ctx.stage?.field;
  const unit = f?.unit > 0 ? f.unit : 0;
  const plane = f?.plane ?? null;
  const at = payload?.uid ? ctx.anchor?.({ card: payload.uid, else: { zone: payload.zone ?? '' } }) : null;
  if (!at?.plane || !(unit > 0) || !plane) return { root };
  const base = Number(f.cardAngle?.(at.zone ?? payload.zone)) || 0;
  const yaw0 = base + turnOf(prev, spell);
  const yaw1 = base + turnOf(next, spell);
  const lift0 = liftOf(prev, ctx.style, spell);
  const thick = { ...OBJECT_STYLE, ...(ctx.style ?? {}) }.cardThickness;
  const [wx, wz] = [at.plane[0] - plane.w / 2, at.plane[1] - plane.h / 2];
  const cw = unit;

  const grp = new THREE.Group();
  add(root, grp, order);
  const inner = new THREE.Group();
  add(grp, inner, order);
  const face = kit.onBoard(card(ctx, { code: prev?.faceUp ? code : null, player: payload.player, faceUp: Boolean(prev?.faceUp), w: cw }).mesh);
  add(inner, face, order + 0.01);
  return {
    root,
    update: ({ localMs: t }) => {
      const m = flipAt(t, cw, spell);
      grp.position.set(wx, lift0 + thick + m.lift, wz);
      grp.rotation.y = yaw0 + (yaw1 - yaw0) * m.yaw;
      // 盤のカードと同じ開き方（長い辺を軸に幅が 1 → 0）
      inner.scale.set(Math.max(0.02, Math.cos(m.theta)), 1, 1);
      fade(face, m.theta < Math.PI / 2 ? 1 : 0);
    },
  };
}
